import { GlobalAlert } from '@thoughtspot/global-alert';
import { translate as t } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum, userActionTypes, } from '@thoughtspot/metrics';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { isAdminModernExperienceEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { BasePageContext } from "../../../contexts/basepage-context";
import { useAddChartMutation, useDeleteChartMutation, useGetAllChartsLazyQuery, useUpdateChartMutation, } from "../../../services/custom-chart/custom-chart-service";
import { DependencyListObjectType, } from "../../../services/generated/graphql-types";
import { useListDependentsMutation } from "../../../services/listdependents/listdependents-service";
import { DEFAULT_ORG_ID as ALL_ORG_ID } from "../../../utils/chart-customization";
import { getParsedDependents } from "../../../utils/dependents";
import { Horizontal } from "../../../utils/layouts";
import { create } from "../../../utils/logger";
import { getErrorInfoWithFallback } from '../error-handling/error-handling-utils';
import styles from './custom-chart.module.scss';
import { CustomChartDeleteModal } from './custom-chart-delete-modal.component';
import { CustomChartFormModal } from './custom-chart-form.component';
import { CustomChartListComponent } from './custom-chart-list.component';
const logger = create('custom-chart-container');
export const CUSTOM_CHARTS_LIMIT = 100;
export const CustomChartContainer = ({ currentOrgId = ALL_ORG_ID, }) => {
    const [showModal, setShowModal] = useState(false);
    const [toastAlertMessage, setToastAlertMessage] = useState('');
    const [customChartToUpdate, setCustomChartToUpdate] = useState({});
    const [handleOperationFn, setHandleOperationFn] = useState(null);
    const [operationError, setOperationError] = useState(null);
    const [customChartIdToBeDeleted, setCustomChartTobeDeleted] = useState(null);
    const [deleteInProgess, setDeleteInProgress] = useState(false);
    const { metricsService } = BasePageContext();
    const { getAllCharts, data: allCharts, loading } = useGetAllChartsLazyQuery(currentOrgId);
    const { addChart, error: addChartError } = useAddChartMutation(currentOrgId);
    const { updateChart, error: updateChartError } = useUpdateChartMutation(currentOrgId);
    const { deleteChart, error: deleteChartError } = useDeleteChartMutation(currentOrgId);
    const { data: dependentsData, error: listDependentsError, loading: listDependentsLoading, useListDependents, } = useListDependentsMutation();
    const closeModal = () => {
        setShowModal(false);
        setCustomChartToUpdate(null);
        setOperationError(null);
    };
    const addChartOperation = async (customChart) => {
        try {
            await addChart(customChart.name, customChart.description ?? '', customChart.sourceUrl, customChart.iconUrl ?? '', customChart.author?.name ?? '', customChart.author?.email ?? '', customChart.author?.organisation ?? '', customChart.chartAppAccessToken ?? '');
            metricsService.collectEvent(new UserAction(userActionTypeEnum.CUSTOM_CHART_ADDED), {
                trackId: userActionTypes.ADD_CUSTOM_CHART.name,
                customChartName: customChart?.name,
                customChartIconUrl: customChart?.iconUrl,
                customChartSourceUrl: customChart?.sourceUrl,
                customChartAuthor: customChart?.author?.name,
                customChartAuthorOrg: customChart?.author?.organisation,
            });
            closeModal();
            setToastAlertMessage(t('customChart.modal.addChart.toast.msg', [customChart.name]));
            getAllCharts();
        }
        catch (e) {
            logger.error(e);
        }
    };
    const handleAddNewChartButtonClick = () => {
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: userActionTypes.ADD_CHART_BUTTON.name,
        });
        setCustomChartToUpdate(null);
        setHandleOperationFn(() => addChartOperation);
        setShowModal(true);
    };
    const editChartOperation = async (customChart) => {
        try {
            await updateChart(customChart.id, customChart.name, customChart.description ?? '', customChart.sourceUrl, customChart.iconUrl ?? '', customChart.author?.name ?? '', customChart.author?.email ?? '', customChart.author?.organisation ?? '', customChart.chartAppAccessToken ?? '');
            metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
                trackId: userActionTypes.UPDATE_CUSTOM_CHART.name,
            });
            closeModal();
            setToastAlertMessage(t('customChart.modal.updateChart.toast.msg', [
                customChart.name,
            ]));
            getAllCharts();
        }
        catch (e) {
            logger.error(e);
        }
    };
    const handleEditChartButtonClick = (chartGuid) => {
        const chartToBeUpdated = allCharts.find(chart => {
            return chart.id === chartGuid;
        });
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: userActionTypes.EDIT_CHART_BUTTON.name,
        });
        setCustomChartToUpdate(_.cloneDeep(chartToBeUpdated));
        setHandleOperationFn(() => editChartOperation);
        setShowModal(true);
    };
    const handleDeleteChartButtonClick = (chartGuid) => {
        const chartToBeUpdated = allCharts.find(chart => {
            return chart.id === chartGuid;
        });
        metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
            trackId: userActionTypes.DELETE_CHART_BUTTON.name,
        });
        setCustomChartTobeDeleted(_.cloneDeep(chartToBeUpdated));
        useListDependents([chartGuid], DependencyListObjectType.File);
        setDeleteInProgress(true);
    };
    const handleOnDeleteModalClose = () => {
        setDeleteInProgress(false);
        setCustomChartTobeDeleted(null);
    };
    const handleOnDeleteConfirmation = async (chartGuid) => {
        try {
            await deleteChart(chartGuid);
            setDeleteInProgress(false);
            setCustomChartTobeDeleted(null);
            metricsService.collectEvent(new UserAction(userActionTypeEnum.CLICK), {
                trackId: userActionTypes.DELETE_CUSTOM_CHART.name,
            });
            setToastAlertMessage(t('customChart.modal.deleteChart.toast.msg', [
                customChartIdToBeDeleted?.name,
            ]));
            getAllCharts();
        }
        catch (e) {
            logger.error(e);
        }
    };
    const dependents = useMemo(() => {
        return getParsedDependents(dependentsData);
    }, [dependentsData]);
    useEffect(() => {
        getAllCharts();
    }, []);
    useEffect(() => {
        const error = getErrorInfoWithFallback(addChartError ?? updateChartError ?? null, t);
        setOperationError(error);
    }, [addChartError, updateChartError]);
    const isAddChartButtonDisabled = allCharts?.length >= CUSTOM_CHARTS_LIMIT;
    return (React.createElement(LoadingIndicator.Global, { hideLoader: !loading && !listDependentsLoading },
        React.createElement(CustomChartFormModal, { isModalOpen: showModal, customChartToBeUpdated: customChartToUpdate, closeModal: closeModal, handleOperation: handleOperationFn, operationError: operationError }),
        React.createElement(GlobalAlert.Toast, { isVisible: !!toastAlertMessage, message: toastAlertMessage, autoHide: true, onClose: () => {
                setToastAlertMessage('');
            } }),
        !listDependentsLoading ? (React.createElement(CustomChartDeleteModal, { customChartIdToBeDeleted: customChartIdToBeDeleted?.id, selectedChartName: customChartIdToBeDeleted?.name, deleteInProgress: deleteInProgess, loading: listDependentsLoading, handleOnDeleteConfirmation: handleOnDeleteConfirmation, handleOnDeleteModalClose: handleOnDeleteModalClose, dependentsData: _.compact(dependents) })) : null,
        React.createElement(Horizontal, { hAlignContent: isAdminModernExperienceEnabled() ? undefined : 'right', vAlignContent: "center", className: isAdminModernExperienceEnabled()
                ? styles.addChartBtnContainerModern
                : styles.addChartBtnContainer },
            isAdminModernExperienceEnabled() && (React.createElement(Typography, { variant: "body-normal", color: Colors.grayLight, noMargin: true }, t('adminSection.chartCustomization.customCharts.description'))),
            React.createElement(Button, { text: t('chartCustomization.customCharts.content.addChart'), tooltip: isAddChartButtonDisabled
                    ? t('chartCustomization.customCharts.addChart.disabled', {
                        noOfMaxCharts: CUSTOM_CHARTS_LIMIT,
                    })
                    : t('chartCustomization.customCharts.content.addChart'), type: 'secondary', size: 'm', backgroundType: 'light', onClick: handleAddNewChartButtonClick, "data-testid": "addChartPrimaryBtn", isDisabled: isAddChartButtonDisabled })),
        React.createElement(CustomChartListComponent, { customChartList: allCharts, onAddChartButtonClick: handleAddNewChartButtonClick, onDeleteChartButtonClick: handleDeleteChartButtonClick, onEditChartButtonClick: handleEditChartButtonClick, loading: loading, currentOrgId: currentOrgId })));
};
//# sourceMappingURL=custom-chart.container.js.map